/*
 * @flow strict
 * Copyright (C) 2022 MetaBrainz Foundation
 *
 * This file is part of MusicBrainz, the open internet music database,
 * and is licensed under the GPL version 2, or (at your option) any
 * later version: http://www.gnu.org/licenses/gpl-2.0.txt
 */

import * as React from 'react';

import Autocomplete2, {
  createInitialState as createInitialAutocompleteState,
} from '../../common/components/Autocomplete2.js';
import {default as autocompleteReducer}
  from '../../common/components/Autocomplete2/reducer.js';
import type {
  ActionT as AutocompleteActionT,
  OptionItemT,
  StateT as AutocompleteStateT,
} from '../../common/components/Autocomplete2/types.js';
import {
  ENTITIES_WITH_RELATIONSHIP_CREDITS,
  ENTITY_NAMES,
  PART_OF_SERIES_LINK_TYPE_GIDS,
} from '../../common/constants.js';
import {
  createNonUrlRelatableEntityObject,
} from '../../common/entity2.js';
import linkedEntities from '../../common/linkedEntities.mjs';
import isDatabaseRowId from '../../common/utility/isDatabaseRowId.js';
import type {
  DialogTargetEntityStateT,
  RelationshipStateT,
  TargetTypeOptionsT,
} from '../types.js';
import type {
  DialogEntityCreditActionT,
  DialogTargetEntityActionT,
  UpdateTargetEntityAutocompleteActionT,
} from '../types/actions.js';
import getRelationshipLinkType from '../utility/getRelationshipLinkType.js';
import isRelationshipBackward from '../utility/isRelationshipBackward.js';

import DialogEntityCredit, {
  createInitialState as createDialogEntityCreditState,
  reducer as dialogEntityCreditReducer,
} from './DialogEntityCredit.js';

const INCORRECT_SERIES_ENTITY_MESSAGES = {
  artist: N_l('The series you’ve selected is for artists.'),
  event: N_l('The series you’ve selected is for events.'),
  recording: N_l('The series you’ve selected is for recordings.'),
  release: N_l('The series you’ve selected is for releases.'),
  release_group: N_l('The series you’ve selected is for release groups.'),
  series: N_l('The series you’ve selected is for series.'),
  work: N_l('The series you’ve selected is for works.'),
};

export function isTargetSelectable(
  target: ?RelatableEntityT,
): implies target is RelatableEntityT {
  return target != null && (
    // `target` is placeholder entity in cases where only a name is set.
    isDatabaseRowId(target.id) ||
    (
      target.entityType === 'work' &&
      target._fromBatchCreateWorksDialog === true
    )
  );
}

export function getTargetError(
  target: ?RelatableEntityT,
  source: RelatableEntityT,
  linkType: ?LinkTypeT,
): string {
  if (!isTargetSelectable(target)) {
    /*
     * Blank fields are handled specially in the dialog (grep
     * `hasBlankRequiredFields`).  To avoid overwhelming the user with
     * "required field" errors, we only highlight the fields red.
     */
    return '';
  }

  if (
    source.entityType === target.entityType &&
    source.id === target.id
  ) {
    return l('Entities in a relationship cannot be the same.');
  }

  if (target.entityType === 'series') {
    const seriesTypeId = target.typeID;
    invariant(
      seriesTypeId != null,
      'Existing series must have a type set',
    );
    const seriesType = linkedEntities.series_type[seriesTypeId];
    const seriesItemType = seriesType.item_entity_type;
    if (
      linkType &&
      PART_OF_SERIES_LINK_TYPE_GIDS.includes(linkType.gid) &&
      seriesItemType !== source.entityType &&
      source.entityType !== 'series'
    ) {
      return INCORRECT_SERIES_ENTITY_MESSAGES[seriesItemType]();
    }
  }

  return '';
}

const returnFalse = () => false;

export function createInitialAutocompleteStateForTarget(options: {
  allowedTypes: TargetTypeOptionsT | null,
  inputRef?: {-current: HTMLInputElement | null},
  relationshipId: number,
  target: NonUrlRelatableEntityT,
}): AutocompleteStateT<NonUrlRelatableEntityT> {
  const {
    allowedTypes,
    inputRef,
    relationshipId,
    target,
  } = options;
  const selectedEntity = isTargetSelectable(target) ? target : null;
  return createInitialAutocompleteState<NonUrlRelatableEntityT>({
    canChangeType: allowedTypes ? (newType) => (
      allowedTypes.some(option => option.value === newType)
    ) : returnFalse,
    containerClass: 'relationship-target',
    entityType: target.entityType,
    id: 'relationship-target-' + String(relationshipId),
    inputChangeHook: selectNewWork,
    inputClass: 'relationship-target',
    inputRef,
    inputValue: target.name,
    required: true,
    selectedItem: selectedEntity ? {
      entity: selectedEntity,
      id: selectedEntity.id,
      name: selectedEntity.name,
      type: 'option',
    } : null,
  });
}

export function createInitialState(options: {
  allowedTypes: TargetTypeOptionsT | null,
  initialFocusRef?: {-current: HTMLElement | null},
  initialRelationship: RelationshipStateT,
  releaseHasUnloadedTracks: boolean,
  source: RelatableEntityT,
}): DialogTargetEntityStateT {
  const {
    allowedTypes,
    initialFocusRef,
    initialRelationship,
    releaseHasUnloadedTracks,
    source,
  } = options;
  const backward = isRelationshipBackward(initialRelationship, source);
  const target = backward
    ? initialRelationship.entity0
    : initialRelationship.entity1;

  let autocomplete = null;
  if (target.entityType !== 'url') {
    autocomplete = createInitialAutocompleteStateForTarget({
      allowedTypes,
      inputRef: initialFocusRef,
      relationshipId: initialRelationship.id,
      target,
    });
  }

  return {
    ...createDialogEntityCreditState(
      backward
        ? initialRelationship.entity0_credit
        : initialRelationship.entity1_credit,
      releaseHasUnloadedTracks,
    ),
    allowedTypes,
    autocomplete,
    error: getTargetError(
      target,
      source,
      getRelationshipLinkType(initialRelationship),
    ),
    relationshipId: initialRelationship.id,
    target,
    targetType: target.entityType,
  };
}

const NEW_WORK_HASH = /#new-work-(-[0-9]+)\s*$/;

function selectNewWork(
  newInputValue: string,
  state: AutocompleteStateT<NonUrlRelatableEntityT>,
  selectItem: (OptionItemT<NonUrlRelatableEntityT>) => boolean,
): boolean {
  const match = newInputValue.match(NEW_WORK_HASH);
  if (match) {
    const newWorkId = match[1];
    const newWork = linkedEntities.work[Number(newWorkId)];
    if (newWork) {
      return selectItem({
        entity: newWork,
        id: newWork.id,
        name: newWork.name,
        type: 'option',
      });
    }
  }
  return false;
}

export function updateTargetAutocomplete(
  newState: {...DialogTargetEntityStateT},
  action: UpdateTargetEntityAutocompleteActionT,
): void {
  invariant(newState.autocomplete);

  newState.autocomplete = autocompleteReducer<NonUrlRelatableEntityT>(
    newState.autocomplete,
    action.action,
  );

  newState.error = getTargetError(
    newState.autocomplete.selectedItem?.entity,
    action.source,
    action.linkType,
  );
}

export function reducer(
  state: DialogTargetEntityStateT,
  action: DialogTargetEntityActionT,
): DialogTargetEntityStateT {
  match (action) {
    {type: 'update-autocomplete', ...} as action => {
      const newState: {...DialogTargetEntityStateT} = {...state};

      updateTargetAutocomplete(newState, action);

      const autocomplete = newState.autocomplete;

      /*:: invariant(autocomplete); */
      /*:: invariant(newState.targetType !== 'url'); */

      newState.targetType = autocomplete.entityType;

      const newTarget = (autocomplete.selectedItem?.entity) ||
        createNonUrlRelatableEntityObject(newState.targetType, {
          name: autocomplete.inputValue,
        });

      if (
        state.target.entityType !== newTarget.entityType ||
        state.target.id !== newTarget.id ||
        state.target.name !== newTarget.name
      ) {
        newState.target = newTarget;
      }

      return newState;
    }
    {type: 'update-credit', const action} => {
      return dialogEntityCreditReducer(
        state,
        action,
      );
    }
    {type: 'update-url-text', const text} => {
      invariant(state.targetType === 'url');

      const newState: {...DialogTargetEntityStateT} = {...state};

      invariant(newState.target.entityType === 'url');

      const newTarget = {...newState.target};
      const url = text;
      newTarget.name = url;
      newState.target = newTarget;

      return newState;
    }
  }
}

component _DialogTargetEntity(
  backward: boolean,
  dispatch: (DialogTargetEntityActionT) => void,
  linkType: ?LinkTypeT,
  source: RelatableEntityT,
  state: DialogTargetEntityStateT,
) {
  const autocomplete = state.autocomplete;
  const targetType = state.targetType;

  if (__DEV__) {
    if (autocomplete) {
      invariant(autocomplete.entityType === targetType);
    }
  }

  const autocompleteDispatch = React.useCallback((
    action: AutocompleteActionT<NonUrlRelatableEntityT>,
  ) => {
    dispatch({
      action,
      linkType,
      source,
      type: 'update-autocomplete',
    });
  }, [dispatch, linkType, source]);

  function handleUrlTextChange(event: SyntheticEvent<HTMLInputElement>) {
    dispatch({
      text: event.currentTarget.value,
      type: 'update-url-text',
    });
  }

  const creditDispatch = React.useCallback((
    action: DialogEntityCreditActionT,
  ) => {
    dispatch({action, type: 'update-credit'});
  }, [dispatch]);

  const showTargetCredit = Boolean(
    ENTITIES_WITH_RELATIONSHIP_CREDITS[targetType] &&
    autocomplete?.selectedItem?.entity,
  );

  return (
    <tr>
      <td className="required section">
        {ENTITY_NAMES[targetType]()}
      </td>
      <td className="fields">
        {targetType === 'url' ? (
          <input
            onChange={handleUrlTextChange}
            type="text"
            value={state.target.name}
          />
        ) : autocomplete ? (
          <Autocomplete2
            dispatch={autocompleteDispatch}
            state={autocomplete}
          />
        ) : null}

        <div className="error">
          {state.error}
        </div>

        {showTargetCredit ? (
          <DialogEntityCredit
            backward={backward}
            dispatch={creditDispatch}
            entityName={state.target.name}
            forEntity="target"
            linkType={linkType}
            state={state}
            targetType={source.entityType}
          />
        ) : null}
      </td>
    </tr>
  );
}

const DialogTargetEntity:
  component(...React.PropsOf<_DialogTargetEntity>) =
    React.memo(_DialogTargetEntity);

export default DialogTargetEntity;
